/**
 * Mini Heatmap
 *
 * A compact heatmap visualization for hourly or categorical data.
 */

import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

interface HeatmapCell {
  /** Cell identifier/label */
  id: string | number;
  /** Display label */
  label: string;
  /** Value for color intensity */
  value: number;
  /** Optional tooltip content */
  tooltip?: string;
}

interface MiniHeatmapProps {
  /** Array of cells to display */
  cells: HeatmapCell[];
  /** Max absolute value for normalization (auto-calculated if not provided) */
  maxValue?: number;
  /** Cell size */
  size?: "sm" | "md" | "lg";
  /** Show labels below cells */
  showLabels?: boolean;
  /** Additional className */
  className?: string;
}

export function MiniHeatmap({
  cells,
  maxValue,
  size = "md",
  showLabels = false,
  className,
}: MiniHeatmapProps) {
  // Calculate max for normalization
  const calculatedMax =
    maxValue || Math.max(...cells.map((c) => Math.abs(c.value)), 1);

  // Height only - width is handled by grid
  const heightClasses = {
    sm: "h-5",
    md: "h-6",
    lg: "h-8",
  }[size];

  return (
    <TooltipProvider delayDuration={100}>
      <div 
        className={cn("grid gap-1 w-full", className)}
        style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}
      >
        {cells.map((cell) => {
          const intensity = Math.min(Math.abs(cell.value) / calculatedMax, 1);
          const isPositive = cell.value > 0;
          const isNegative = cell.value < 0;

          const baseOpacity = 0.15;
          const maxOpacity = 0.7;
          const opacity = baseOpacity + intensity * (maxOpacity - baseOpacity);

          const bgColor = isPositive
            ? `rgba(46, 189, 133, ${opacity})`
            : isNegative
            ? `rgba(246, 70, 93, ${opacity})`
            : "transparent";

          return (
            <Tooltip key={cell.id}>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center gap-0.5">
                  <div
                    className={cn(
                      "rounded-sm transition-transform hover:scale-110 cursor-default w-full",
                      heightClasses
                    )}
                    style={{ backgroundColor: bgColor }}
                  />
                  {showLabels && (
                    <span className="text-[8px] text-muted-foreground">
                      {cell.label}
                    </span>
                  )}
                </div>
              </TooltipTrigger>
              {cell.tooltip && (
                <TooltipContent side="top" className="text-xs">
                  {cell.tooltip}
                </TooltipContent>
              )}
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

/**
 * Horizontal bar for single value comparison
 */
interface ComparisonBarProps {
  leftLabel: string;
  rightLabel: string;
  leftValue: number;
  rightValue: number;
  className?: string;
}

export function ComparisonBar({
  leftLabel,
  rightLabel,
  leftValue,
  rightValue,
  className,
}: ComparisonBarProps) {
  const total = leftValue + rightValue;
  const leftPercent = total > 0 ? (leftValue / total) * 100 : 50;

  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex justify-between text-sm font-medium text-foreground">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden flex">
        <div
          className="h-full bg-bid-foreground/70 transition-all"
          style={{ width: `${leftPercent}%` }}
        />
        <div
          className="h-full bg-ask-foreground/70 transition-all"
          style={{ width: `${100 - leftPercent}%` }}
        />
      </div>
      <div className="flex justify-between text-xs font-medium">
        <span className="text-foreground">{leftValue}</span>
        <span className="text-foreground">{rightValue}</span>
      </div>
    </div>
  );
}

/**
 * Simple progress bar
 */
interface ProgressBarProps {
  value: number;
  max?: number;
  variant?: "default" | "positive" | "negative";
  showLabel?: boolean;
  className?: string;
}

export function ProgressBar({
  value,
  max = 100,
  variant = "default",
  showLabel = false,
  className,
}: ProgressBarProps) {
  const percent = Math.min((value / max) * 100, 100);

  const colorClass = {
    default: "bg-primary",
    positive: "bg-bid-foreground",
    negative: "bg-ask-foreground",
  }[variant];

  return (
    <div className={cn("space-y-1", className)}>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all", colorClass)}
          style={{ width: `${percent}%` }}
        />
      </div>
      {showLabel && (
        <div className="text-xs text-muted-foreground text-right">
          {value.toFixed(0)}%
        </div>
      )}
    </div>
  );
}

